import { useForm } from '@mantine/form';
import {
  ConnectorType,
  IsEVCCIDSupported,
  PowerType,
} from '../../../models/ChargingConnector.ts';
import {
  Button,
  Checkbox,
  Group,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import React from 'react';
import { ConnectorTypeMap } from '../../../const/charging_connector_type_mapping.ts';

export interface MutateChargingConnectorFormValues {
  connectorSerialNumber: string;
  connectorType: ConnectorType | null;
  maxPowerValue: number | null;
  powerType: PowerType | null;
  isEVCCIDSupported: IsEVCCIDSupported | null;
  rateText: string;
}

interface MutateChargingConnectorFormProps {
  initialValues: MutateChargingConnectorFormValues;
  onSubmit: (formValues: MutateChargingConnectorFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
}

export default function MutateChargingConnectorForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitButtonText,
}: MutateChargingConnectorFormProps) {
  const form = useForm<MutateChargingConnectorFormValues>({
    mode: 'controlled',
    initialValues: {
      connectorSerialNumber: initialValues.connectorSerialNumber,
      connectorType: initialValues.connectorType,
      maxPowerValue: initialValues.maxPowerValue,
      powerType: initialValues.powerType,
      isEVCCIDSupported: initialValues.isEVCCIDSupported,
      rateText: initialValues.rateText,
    },
    validate: {
      connectorType: (value) => {
        if (!value) return ' ';
      },
      maxPowerValue: (value) => {
        if (!value) return ' ';
        if (value <= 0) return '數值不可為負';
      },
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        {/* 充電槍編號 */}
        <TextInput
          label={'充電槍編號'}
          placeholder={'請輸入充電槍編號'}
          key={form.key('connectorSerialNumber')}
          {...form.getInputProps('connectorSerialNumber')}
          styles={{
            input: {
              width: 627,
              borderColor: form.errors.connectorSerialNumber
                ? 'var(--mantine-color-red-6)'
                : undefined,
            },
          }}
        />

        <Group gap={16} style={{ paddingTop: 16, paddingBottom: 16 }}>
          {/* 充電槍功率 */}
          <TextInput
            label={'充電槍功率(kW)'}
            placeholder={'請輸入充電槍功率(kW)'}
            withAsterisk
            key={form.key('maxPowerValue')}
            {...form.getInputProps('maxPowerValue')}
            styles={{
              input: {
                width: 305.5,
                borderColor: form.errors.maxPowerValue
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
          {/* 充電槍規格 */}
          <Select
            label={'充電槍規格'}
            placeholder={'請選擇充電槍規格'}
            withAsterisk
            data={Object.values(ConnectorTypeMap).map((connectorType) => {
              return {
                value: connectorType.value,
                label: connectorType.label,
              };
            })}
            key={form.key('connectorType')}
            {...form.getInputProps('connectorType')}
            onChange={(value) =>
              form.setFieldValue('connectorType', value as ConnectorType)
            }
            styles={{
              input: {
                width: 305.5,
                borderColor: form.errors.connectorType
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
        </Group>

        <Group gap={16} style={{ paddingBottom: 16 }}>
          <div style={{ height: 50, width: 305.5 }}>
            <Stack gap={8}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>電力輸出方式</div>
              <Group gap={12}>
                <Group gap={12}>
                  {/* DC */}
                  <Checkbox
                    radius="xl"
                    label="DC"
                    checked={form.values.powerType === 'DC'}
                    onChange={() => {
                      form.setFieldValue(
                        'powerType',
                        form.values.powerType === 'DC' ? null : 'DC',
                      );
                    }}
                    styles={{ label: { paddingLeft: 8 } }}
                  />

                  {/* AC */}
                  <Checkbox
                    radius="xl"
                    label="AC"
                    checked={form.values.powerType === 'AC'}
                    onChange={() => {
                      form.setFieldValue(
                        'powerType',
                        form.values.powerType === 'AC' ? null : 'AC',
                      );
                    }}
                    styles={{ label: { paddingLeft: 8 } }}
                  />
                </Group>
              </Group>
            </Stack>
          </div>
        </Group>

        <Textarea
          rows={4}
          label={'充電費率'}
          placeholder={'請輸入充電費率'}
          key={form.key('rateText')}
          {...form.getInputProps('rateText')}
        />

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group justify="flex-end" mt="24px">
          <Button variant="outline" onClick={handleCancel}>
            關閉
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {submitButtonText}
          </Button>
        </Group>
      </form>
    </>
  );
}
